import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { financeApi } from '../../services/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { HiOutlineTrendingUp, HiOutlineTrendingDown, HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi';
import TruncatedName from '../../components/TruncatedName';
import {
  formatMonthLongCalendarMonth,
  formatMonthShortFromIndex0,
  formatCalendarDateDMY,
  getCalendarNowIST,
} from '../../utils/timezoneDisplay';

function monthYearBounds(m, y) {
  const last = new Date(y, m, 0).getDate();
  return {
    date_from: `${y}-${String(m).padStart(2, '0')}-01`,
    date_to: `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`,
  };
}

function formatReportPeriodLabel(period) {
  if (!period) return '';
  if (period.type === 'range') {
    return `${period.date_from} → ${period.date_to}`;
  }
  return `${formatMonthLongCalendarMonth(period.month)} ${period.year}`;
}

const Finance = () => {
  const istNow = getCalendarNowIST();
  const [dashboard, setDashboard] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsModal, setDetailsModal] = useState(null);
  const [showExpenseDetails, setShowExpenseDetails] = useState(false);
  const [showRevenueDetails, setShowRevenueDetails] = useState(false);

  const [filterMode, setFilterMode] = useState('month');
  const [month, setMonth] = useState(istNow.month);
  const [year, setYear] = useState(istNow.year);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const reportPeriod = dashboard?.reportPeriod || profitLoss?.period;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let dashboardParams = {};
      let plParams = {};
      if (filterMode === 'range') {
        const fb = monthYearBounds(month, year);
        const from = (dateFrom && String(dateFrom).trim()) || fb.date_from;
        const to = (dateTo && String(dateTo).trim()) || fb.date_to;
        if (from > to) {
          toast.error('Start date must be on or before end date.');
          setLoading(false);
          return;
        }
        dashboardParams = { date_from: from, date_to: to };
        plParams = { date_from: from, date_to: to };
      } else {
        dashboardParams = { month, year };
        plParams = { month, year };
      }
      const [dashboardRes, plRes] = await Promise.all([
        financeApi.getDashboard(dashboardParams),
        financeApi.getProfitLoss(plParams),
      ]);
      setDashboard(dashboardRes.data.data);
      setProfitLoss(plRes.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch finance data');
    } finally {
      setLoading(false);
    }
  }, [filterMode, month, year, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const switchToRangeWithMonthBounds = () => {
    const { date_from, date_to } = monthYearBounds(month, year);
    setDateFrom(date_from);
    setDateTo(date_to);
    setFilterMode('range');
  };

  const periodSubtitle = useMemo(() => formatReportPeriodLabel(reportPeriod), [reportPeriod]);
  const paymentsInPeriod = dashboard?.monthPaymentsDetail || [];
  const pendingReceivablesDetail = dashboard?.pendingReceivablesDetail || [];

  const paymentTypeBreakdown = useMemo(() => {
    const grouped = paymentsInPeriod.reduce((acc, payment) => {
      const key = payment.payment_type || 'other';
      acc[key] = (acc[key] || 0) + parseFloat(payment.amount || 0);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([label, amount]) => ({ label, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [paymentsInPeriod]);

  const pendingStatusBreakdown = useMemo(() => {
    const grouped = pendingReceivablesDetail.reduce((acc, invoice) => {
      const key = invoice.status || 'unknown';
      acc[key] = (acc[key] || 0) + parseFloat(invoice.balance_amount || 0);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([label, amount]) => ({ label, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [pendingReceivablesDetail]);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Finance Dashboard</h1>
        <p className="text-gray-500 mt-1">Revenue, expenses, and profit/loss analysis</p>
      </div>

      {/* Report period filters — single row */}
      <div
        className="card px-3 py-2.5"
        title={
          filterMode === 'range'
            ? 'Totals and lists use payment/expense dates in the range. Payroll salary in P&L only if not already posted as an expense (PAYROLL_REF).'
            : 'Totals and lists use the selected calendar month. Today card is always actual today.'
        }
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-medium text-gray-700 shrink-0">Report</span>
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 shrink-0">
            <button
              type="button"
              onClick={() => setFilterMode('month')}
              className={`px-2.5 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                filterMode === 'month' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Month
            </button>
            <button
              type="button"
              onClick={switchToRangeWithMonthBounds}
              className={`px-2.5 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                filterMode === 'range' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Date range
            </button>
          </div>

          {filterMode === 'month' ? (
            <>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm min-w-[7.5rem]"
                aria-label="Month"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {formatMonthShortFromIndex0(i)}
                  </option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm min-w-[4.5rem]"
                aria-label="Year"
              >
                {Array.from({ length: 15 }, (_, i) => {
                  const y = istNow.year - 10 + i;
                  return (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  );
                })}
              </select>
            </>
          ) : (
            <>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                aria-label="From date"
              />
              <span className="text-gray-400 text-sm shrink-0">→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                aria-label="To date"
              />
            </>
          )}

          {periodSubtitle && (
            <span
              className="text-sm text-primary-700 font-medium ml-auto min-w-0 max-w-full sm:max-w-[14rem] truncate"
              title={periodSubtitle}
            >
              {periodSubtitle}
            </span>
          )}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500">Today's Collection</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{(dashboard?.todayCollections || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">Always today (not filtered)</p>
        </div>
        <button
          type="button"
          onClick={() => setDetailsModal('collections')}
          className="card text-left transition-all hover:ring-2 hover:ring-gray-200"
        >
          <p className="text-sm text-gray-500">
            {reportPeriod?.type === 'range' ? 'Collections (range)' : 'Collections (period)'}
          </p>
          <p className="text-2xl font-bold text-green-600">
            ₹{(dashboard?.monthCollections || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">Click to view collection bifurcation</p>
        </button>
        <button
          type="button"
          onClick={() => setDetailsModal('pending')}
          className="card text-left transition-all hover:ring-2 hover:ring-gray-200"
        >
          <p className="text-sm text-gray-500">Pending Receivables</p>
          <p className="text-2xl font-bold text-amber-600">
            ₹{(dashboard?.pendingReceivables || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">Click to view pending bifurcation</p>
        </button>
        <div className="card">
          <p className="text-sm text-gray-500">
            {reportPeriod?.type === 'range' ? 'Expenses (range)' : 'Expenses (period)'}
          </p>
          <p className="text-2xl font-bold text-red-600">
            ₹{(dashboard?.monthExpenses || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Profit/Loss and Expense Categories */}
      {profitLoss && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Profit & Loss — {formatReportPeriodLabel(profitLoss.period)}
              </h3>
              <button
                onClick={() => setShowRevenueDetails(!showRevenueDetails)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {showRevenueDetails ? (
                  <>
                    <HiOutlineChevronUp className="w-4 h-4" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <HiOutlineChevronDown className="w-4 h-4" />
                    Show Details
                  </>
                )}
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-green-700">Total Revenue</span>
                <span className="font-bold text-green-700">
                  ₹{(profitLoss.revenue?.total_revenue || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-red-700">Total Expenses</span>
                <span className="font-bold text-red-700">
                  ₹{(profitLoss.totalExpenses || 0).toLocaleString()}
                </span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded-lg ${
                profitLoss.profit >= 0 ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                <span className={profitLoss.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                  {profitLoss.profit >= 0 ? 'Net Profit' : 'Net Loss'}
                </span>
                <div className="flex items-center gap-2">
                  {profitLoss.profit >= 0 ? (
                    <HiOutlineTrendingUp className="w-5 h-5 text-emerald-700" />
                  ) : (
                    <HiOutlineTrendingDown className="w-5 h-5 text-red-700" />
                  )}
                  <span className={`font-bold ${profitLoss.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    ₹{Math.abs(profitLoss.profit).toLocaleString()} ({profitLoss.profitPercentage}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Detailed Revenue List */}
            {showRevenueDetails && dashboard?.monthPaymentsDetail && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  Payments in period ({dashboard.monthPaymentsDetail.length} transactions)
                </h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {dashboard.monthPaymentsDetail.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No payments in this period</p>
                  ) : (
                    dashboard.monthPaymentsDetail.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{payment.patient_name}</span>
                            <span className="text-xs text-gray-500">({payment.patient_code})</span>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {payment.payment_type || 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span>{formatCalendarDateDMY(payment.payment_date)}</span>
                            {payment.invoice_number && <span>• Invoice: {payment.invoice_number}</span>}
                            {payment.payment_mode && <span>• {payment.payment_mode}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">₹{parseFloat(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          {payment.invoice_total && (
                            <p className="text-xs text-gray-500 mt-1">
                              Invoice: ₹{parseFloat(payment.invoice_total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total Revenue:</span>
                  <span className="font-bold text-green-600 text-lg">
                    ₹{dashboard.monthPaymentsDetail.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Expense breakdown */}
          {profitLoss?.expenses && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Expense Categories</h3>
                <button
                  onClick={() => setShowExpenseDetails(!showExpenseDetails)}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  {showExpenseDetails ? (
                    <>
                      <HiOutlineChevronUp className="w-4 h-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <HiOutlineChevronDown className="w-4 h-4" />
                      Show Details
                    </>
                  )}
                </button>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={profitLoss.expenses.filter(e => e.amount > 0)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(v) => `₹${v/1000}K`} />
                    <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                    <Bar dataKey="amount" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Detailed Expense List */}
              {showExpenseDetails && dashboard?.monthExpensesDetail && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Expenses in period ({dashboard.monthExpensesDetail.length} items)
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {dashboard.monthExpensesDetail.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No expenses in this period</p>
                    ) : (
                      dashboard.monthExpensesDetail.map((expense) => (
                        <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{expense.description || 'No description'}</span>
                              <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                                {expense.category_name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>{formatCalendarDateDMY(expense.expense_date)}</span>
                              {expense.vendor && <span>• Vendor: {expense.vendor}</span>}
                              {expense.payment_mode && <span>• {expense.payment_mode}</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">₹{parseFloat(expense.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            {expense.status && (
                              <p className={`text-xs mt-1 ${
                                expense.status === 'approved' ? 'text-green-600' :
                                expense.status === 'pending' ? 'text-amber-600' :
                                'text-gray-600'
                              }`}>
                                {expense.status}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Expenses:</span>
                    <span className="font-bold text-red-600 text-lg">
                      ₹{dashboard.monthExpensesDetail.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent payments (up to 5 in selected period) */}
      {dashboard?.recentPayments?.length > 0 && (
        <div className="card">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900">Recent payments</h3>
            {periodSubtitle && <p className="text-xs text-gray-500 mt-0.5">{periodSubtitle}</p>}
          </div>
          <div className="space-y-3">
            {dashboard.recentPayments.map(payment => (
              <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <TruncatedName name={payment.patient_name} maxLength={20}>
                    <p className="font-medium" />
                  </TruncatedName>
                  <p className="text-sm text-gray-500">{payment.invoice_number}</p>
                </div>
                <p className="font-bold text-green-600">₹{parseFloat(payment.amount).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {detailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white w-full max-w-5xl rounded-xl shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {detailsModal === 'collections' ? 'Collections bifurcation' : 'Pending receivables bifurcation'}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {detailsModal === 'collections'
                    ? `Total: ₹${(dashboard?.monthCollections || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : `Total: ₹${(dashboard?.pendingReceivables || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetailsModal(null)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            {detailsModal === 'collections' ? (
              <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">By payment type</h4>
                  {paymentTypeBreakdown.length === 0 ? (
                    <p className="text-sm text-gray-500">No collections in selected period.</p>
                  ) : (
                    paymentTypeBreakdown.map((item) => (
                      <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg bg-green-50">
                        <span className="text-sm text-gray-700 capitalize">{item.label.replaceAll('_', ' ')}</span>
                        <span className="font-semibold text-green-700">
                          ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Transactions ({paymentsInPeriod.length})</h4>
                  <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
                    {paymentsInPeriod.length === 0 ? (
                      <p className="text-sm text-gray-500">No payment transactions.</p>
                    ) : (
                      paymentsInPeriod.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{payment.patient_name}</p>
                            <p className="text-xs text-gray-500">
                              {payment.invoice_number || '-'} • {formatCalendarDateDMY(payment.payment_date)}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-green-600">
                            ₹{parseFloat(payment.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">By invoice status</h4>
                  {pendingStatusBreakdown.length === 0 ? (
                    <p className="text-sm text-gray-500">No pending receivables.</p>
                  ) : (
                    pendingStatusBreakdown.map((item) => (
                      <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50">
                        <span className="text-sm text-gray-700 capitalize">{item.label}</span>
                        <span className="font-semibold text-amber-700">
                          ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Invoices ({pendingReceivablesDetail.length})</h4>
                  <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
                    {pendingReceivablesDetail.length === 0 ? (
                      <p className="text-sm text-gray-500">No pending invoices.</p>
                    ) : (
                      pendingReceivablesDetail.map((invoice) => (
                        <div key={invoice.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{invoice.patient_name}</p>
                            <p className="text-xs text-gray-500">
                              {invoice.invoice_number || '-'} • Due: {invoice.due_date ? formatCalendarDateDMY(invoice.due_date) : '-'}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-amber-600">
                            ₹{parseFloat(invoice.balance_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Finance;

